/**
 * 统计用SDK封装层，封装斐波SDK
 * @author wangxuejin
 * @since 2021-3-6
 */

import * as fiboSDK from 'utils/fiboSDK';
import { InitCallback } from 'utils/fiboSDK';
import { getMobileCollect } from 'api/user';

/**
 * 初始化统计SDK
 */
export function init(initCallback: InitCallback) {
  fiboSDK.init(initCallback);
}

/**
 * fiboSDK推送自定义事件
 * @export
 * @param {string} eventType 事件类型
 * @param {Record<string, any>} evetArgs 事件属性
 */
export function pushCustomEvent(
  eventType: string,
  evetArgs: Record<string, any>
) {
  if (!fiboSDK) return;
  fiboSDK.pushCustomEvent(eventType, evetArgs);
}

/** 登录账号埋点 */
export function pushUserLogin() {
  pushCustomEvent('c_login', {});
}

/** 注册账号埋点 */
export function pushUserRegister() {
  pushCustomEvent('c_register', {});
}

/**
 * 网站级事件 - 访问网站
 */
export function pushWebInitEvent() {
  pushCustomEvent('c_webvisit', {});
}

/**
 * 网站级事件 - 移动端引流
 */

export async function pushMobilenitEvent() {
  const collectStatus = await getMobileCollect();
  if (collectStatus && collectStatus.reportEvents) {
    pushCustomEvent('c_KPI', {
      c_elmname: '移动端模板引流成功',
    });
  }
}
/**
 * 暴露fibo方法到navbar使用
 */
export function exportFibo() {
  if (!fiboSDK) {
    return;
  }
  // 给navbar传入当前fibo方法
  const navbarInitFibo =
    window.COMMON_NAVBAR && window.COMMON_NAVBAR.navbarInitFibo;
  navbarInitFibo && navbarInitFibo(pushCustomEvent);
  // 给footer传入当前fibo方法
  const footerInitFibo =
    window.COMMON_FOOTER && window.COMMON_FOOTER.footerInitFibo;
  footerInitFibo && footerInitFibo(pushCustomEvent);
}

/**
 * 页面级 - 浏览页面
 */
export function pushPageViewEvent() {
  // rurl 前向路径 跳转前的页面，也即refer的页面路径（去除参数）
  const route = fiboSDK.getPageInfo(document.referrer).route;
  pushCustomEvent('c_pageview', {
    rurl: route,
  });
}

/**
 * 埋点-模板筛选项
 */
export function pushFiboTemplateFilter(params: Record<string, any>) {
  const { fieldName, text, filtersList } = params;
  // 当前点击筛选项文本
  let elementName = '';
  const activeIndex =
    (filtersList &&
      filtersList.findIndex(
        (item: Record<string, any>) => item.key === fieldName
      )) ||
    0;
  if (activeIndex < 0) {
    // 下拉框筛选项
    filtersList
      .filter((e: Record<string, any>) => e.type === 'selection')
      .map((item: Record<string, any>) => {
        item.subFilters.forEach((subItem: Record<string, any>) => {
          if (subItem.key === fieldName) {
            elementName = subItem.name;
          }
        });
      });
  } else {
    elementName = filtersList[activeIndex].name || '';
  }

  pushCustomEvent('c_click', {
    c_elmname: elementName,
    c_elmcontent: text,
    c_elmtype: 'button',
    c_component: 'Select',
  });
}

/**
 * 模板收藏
 */
export function pushFiboTemplateCollect(params: Record<string, any>) {
  const { type, title, tplPrice, isPluginType = false } = params;
  let priceType = '';
  if (!isPluginType) {
    priceType = `-${tplPrice ? '付费' : '免费'}`;
  }
  pushCustomEvent('c_click', {
    c_elmname: type ? '收藏' : '取消收藏',
    c_elmcontent: `${title}${priceType}`,
    c_elmtype: 'button',
    c_component: 'Template',
  });
}

/**
 * 购买成功
 */
export function pushFiboTemplateBuy(params: Record<string, any>) {
  const { title, price } = params;
  pushCustomEvent('c_paid', {
    c_elmname: title,
    c_money: price || '',
    c_elmtype: 'button',
    c_component: 'TemplateLayer',
  });
}

/**
 * 去制作
 */
export function pushTemplateToMake(params: Record<string, any>) {
  const { title, tplPrice } = params;
  const isFree = tplPrice === 0 || tplPrice === null;
  const tplContent = `${title}-${isFree ? '免费' : '付费'}`;
  pushCustomEvent('c_click', {
    c_elmname: '去制作',
    c_elmcontent: tplContent,
    c_elmtype: 'button',
    c_component: 'TemplateLayer',
  });
}

/**
 * 推荐页底部-查看更多模板
 */
export function pushFiboBottomTemplate() {
  pushCustomEvent('c_click', {
    c_elmname: '查看更多模板',
    c_elmtype: 'button',
    c_component: 'Bottom',
  });
}

/**
 * 推荐页-查看更多模板
 */
export function pushFiboMoreTemplate(title: string) {
  pushCustomEvent('c_click', {
    c_elmname: `${title}-查看更多`,
    c_elmtype: 'button',
    c_component: 'MoreDetail',
  });
}

/**
 * 右侧栏-模板专题
 */
export function pushFiboRightNavTopic(title: string) {
  pushCustomEvent('c_click', {
    c_elmname: title,
    c_elmtype: 'button',
    c_component: 'Widget',
  });
}

/**
 * 模板市场-专题图
 */
export function pushFiboTopicImage(title: string) {
  pushCustomEvent('c_click', {
    c_elmname: title,
    c_elmtype: 'img',
    c_component: 'Banner',
  });
}

/**
 * 模板市场-搜索点击
 */
export function pushFiboSearchKeyWord(keyword: string) {
  pushCustomEvent('c_click', {
    c_elmname: '搜索框',
    c_elmcontent: keyword,
    c_elmtype: 'input',
    c_component: 'Search',
  });
}

/**
 * 模板市场-搜索热词
 */
export function pushFiboSearchHotWord(title: string) {
  pushCustomEvent('c_click', {
    c_elmname: '热门搜索',
    c_elmcontent: title,
    c_elmtype: 'button',
    c_component: 'Search',
  });
}

/**
 * 埋点-导航
 */
export function pushFiboNavBarClick(title: string) {
  pushCustomEvent('c_click', {
    c_elmname: title,
    c_elmtype: 'button',
    c_component: 'FirstMenu',
  });
}

/**
 * 埋点-模板市场-二级导航栏
 */
export function pushFiboSubNavBarClick(title: string) {
  pushCustomEvent('c_click', {
    c_elmname: title,
    c_elmtype: 'button',
    c_component: 'SecondMenu',
  });
}

/**
 * 埋点-监听导航点击事件
 */
export function initPushFiboNavBarClick() {
  const links = document.querySelectorAll(
    '.g-common-navbar-box .g-common-navbar-list'
  );
  Array.from(links).forEach(link => {
    link.addEventListener('click', (event: Event) => {
      const node = event.target as HTMLElement;
      if (node && node.nodeName === 'A') {
        event.preventDefault();
        const title = node.innerText;
        const link = node.getAttribute('href') || '/';
        const linkTarget = node.getAttribute('target');
        pushFiboNavBarClick(title);
        if (linkTarget === '_self') {
          setTimeout(() => {
            window.location.href = link;
          }, 200);
        } else {
          window.open(link, '_blank');
        }
      }
    });
  });
}

/**
 * 埋点-二级导航栏事件绑定
 */
export function initSubNavBarClick() {
  const subLink = document.querySelectorAll(
    '.g-common-navbar-box .g-common-navbar .g-common-navbar-sub .sub-list'
  );
  Array.from(subLink).forEach(link => {
    link.addEventListener('click', (event: Event) => {
      const node = event.target as HTMLElement;
      if (node && node.nodeName === 'A') {
        event.preventDefault();
        const title = node.innerText || '';
        const link = node.getAttribute('href') || '/';
        pushFiboSubNavBarClick(title);
        setTimeout(() => {
          window.location.href = link;
        }, 200);
      }
    });
  });
}

/**
 * 埋点-插屏消息弹窗点击
 */
export function initFiboMsgPopupClick() {
  setTimeout(() => {
    const $link = document.querySelector(
      '.g-common-popup-overlay .g-common-popup .g-common-popup-body'
    );
    $link &&
      $link.addEventListener('click', evt => {
        const target = evt.target;
        const title = (target as HTMLLIElement).getAttribute('alt') || '';
        pushCustomEvent('c_click', {
          c_elmname: title,
          c_elmtype: 'img',
          c_component: 'PopUp',
        });
      });
  }, 800);
}
